import { useEffect, useState } from 'react';

/**
 * Generates a poster thumbnail (data URL) from the first frame of a selected video File,
 * entirely client-side — no upload/round-trip needed. Returns `undefined` for non-video
 * files, while generating, or when the browser can't decode the frame (callers should show
 * a placeholder in that case).
 *
 * The frame is captured slightly past t=0 to avoid the common black/blank first frame.
 */
export default function useVideoThumbnail(file?: File): string | undefined {
  const [thumbnail, setThumbnail] = useState<string>();

  useEffect(() => {
    setThumbnail(undefined);

    if (!file || !file.type?.startsWith('video/') || typeof URL.createObjectURL !== 'function') {
      return;
    }

    let cancelled = false;
    const objectUrl = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.src = objectUrl;

    const cleanup = () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('seeked', handleSeeked);
      video.removeEventListener('error', cleanup);
      video.removeAttribute('src');
      video.load();
      URL.revokeObjectURL(objectUrl);
    };

    function handleLoadedMetadata() {
      // Seek a touch past the start so we don't capture a black leading frame.
      const seekTo = Math.min(0.1, (video.duration || 1) / 2);
      video.currentTime = Number.isFinite(seekTo) ? seekTo : 0;
    }

    function handleSeeked() {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        if (ctx && canvas.width && canvas.height) {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
          if (!cancelled) {
            setThumbnail(dataUrl);
          }
        }
      } catch {
        // Decoding/canvas can fail (e.g. unsupported codec) — leave the placeholder in place.
      } finally {
        cleanup();
      }
    }

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('seeked', handleSeeked);
    video.addEventListener('error', cleanup);

    return () => {
      cancelled = true;
      cleanup();
    };
  }, [file]);

  return thumbnail;
}
